import { useId, useState, type FormEvent, type ReactNode } from "react";
import { Eye, EyeOff, AlertCircle, Info } from "../../atoms/Icon";
import { cn } from "../../utils";

export interface SplitLoginProps {
  /** 우측 폼 상단 제목 (예: "로그인") */
  title?: string;
  /** 제목 아래 보조 설명 */
  subtitle?: string;
  /** 로고 아래 뱃지 (예: "관리자 전용"). 없으면 미노출 */
  badge?: string;
  /**
   * 브랜드명 (좌측 페인 + 우측 로고). 기본 "SAFERS".
   * 빈 문자열이면 이름·태그라인 글자를 통째로 숨긴다 — `logo` 에 워드마크까지 든
   * 락업을 넘겨 글자가 두 번 나오는 걸 피할 때 쓴다.
   */
  brandName?: string;
  /** 좌측 페인 상단 보조 텍스트 (예: "Admin Console") */
  brandTagline?: string;
  /**
   * 브랜드 로고. 넘기면 `brandName` 첫 글자 박스 대신 이것을 그린다.
   * 좌측 페인(항상 어두움)과 우측 폼(테마 배경) 양쪽에 같은 노드가 쓰이므로
   * 배경색을 가정하지 않는 로고를 넘길 것 — `currentColor` 기반이면 가장 안전하다.
   */
  logo?: ReactNode;
  /**
   * 좌측 페인 배경 사진 URL. 없으면 브랜드 그라디언트로 폴백한다.
   * 넘기면 텍스트 대비 확보용 스크림이 사진 위에 자동으로 덮인다.
   */
  heroImage?: string;
  /** 좌측 페인 큰 메시지 */
  heroTitle?: ReactNode;
  /** 좌측 페인 본문 */
  heroDescription?: ReactNode;
  /** 우측 폼 맨 아래 푸터 (예: "© Pluxity · SAFERS Admin v0.1") */
  footer?: ReactNode;
  /** 폼 하단 안내 문구 */
  helpText?: ReactNode;
  loading?: boolean;
  /** 외부에서 주입하는 에러 메시지 (인증 실패 등) */
  error?: string | null;
  /** 에러가 아닌 안내 (비밀번호 변경 후 다시 로그인 등). 에러가 있으면 에러만 보인다 */
  notice?: string | null;
  onSubmit: (data: { username: string; password: string; remember: boolean }) => void;
}

/**
 * 분할 로그인 화면 — 좌측 브랜드 페인 + 우측 카드 폼.
 * UI 전용. 인증 흐름/권한 가드는 onSubmit 을 받는 호출부(app/admin)가 맡는다.
 *
 * 좌측 페인은 앱 테마 밖이라 늘 어둡다. 그 안의 글자에 `--text-*` 토큰을 쓰면
 * 라이트 테마에서 흰 글자가 흰 배경에 사라지므로 흰색 알파를 직접 쓴다.
 */
export function SplitLogin({
  title = "로그인",
  subtitle,
  badge,
  brandName = "SAFERS",
  brandTagline,
  logo,
  heroImage,
  heroTitle,
  heroDescription,
  footer,
  helpText,
  loading = false,
  error,
  notice,
  onSubmit,
}: SplitLoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onSubmit({ username: username.trim(), password, remember });
  }

  const brandMark = logo ?? <InitialMark brandName={brandName} />;

  return (
    <div className="flex min-h-screen bg-[var(--surface-base)]">
      {/* 좌측 브랜드 페인 — lg 이상에서만 */}
      <aside className="relative hidden lg:flex lg:w-[44%] xl:w-[48%] flex-col overflow-hidden bg-[image:var(--login-hero-bg)] p-12 text-white">
        {heroImage ? (
          <>
            <div
              className="pointer-events-none absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url("${heroImage}")` }}
            />
            {/* 사진 위 글자 대비용 스크림 */}
            <div className="pointer-events-none absolute inset-0 bg-[image:var(--login-hero-scrim)]" />
          </>
        ) : (
          /* 사진이 없을 때의 장식 — 브랜드 그라디언트 위 동심원 + 스카이라인 */
          <>
            <div className="pointer-events-none absolute -top-40 -left-40 h-[32.5rem] w-[32.5rem] rounded-full border border-white/10" />
            <div className="pointer-events-none absolute -top-20 -left-20 h-[22.5rem] w-[22.5rem] rounded-full border border-white/[0.06]" />
            <div className="pointer-events-none absolute -bottom-48 -right-32 h-[38.75rem] w-[38.75rem] rounded-full border border-[var(--brand-primary)]/20" />
            <div className="pointer-events-none absolute bottom-10 right-20 h-[11.25rem] w-[11.25rem] rounded-full bg-[var(--brand-primary)]/[0.08] blur-2xl" />
            <Skyline />
          </>
        )}

        {/* 우측 폼이 같은 락업을 이미 읽어 준다. 여기서는 화면에만 남긴다 */}
        <div aria-hidden="true" className="relative z-10 flex items-center gap-3">
          {brandMark}
          {brandName && (
            <div className="leading-tight">
              <div className="text-[0.9375rem] font-semibold">{brandName}</div>
              {brandTagline && <div className="text-[0.6875rem] text-white/60">{brandTagline}</div>}
            </div>
          )}
        </div>

        {/* 아래 절반은 장식·사진 몫이라 문구는 가운데에 둔다 */}
        <div className="relative z-10 my-auto max-w-md">
          {heroTitle && <div className="text-[1.75rem] font-bold leading-[1.25]">{heroTitle}</div>}
          {heroDescription && (
            <div className="mt-4 text-[0.8125rem] leading-[1.7] text-white/70">
              {heroDescription}
            </div>
          )}
        </div>
      </aside>

      {/* 우측 폼 */}
      <main className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {/* 뱃지를 같은 줄에 두면 로고가 밀리거나(흐름) 락업과 겹친다(절대배치) */}
          <div className="mb-7 flex flex-col items-center gap-2.5">
            <div className="flex items-center gap-2.5">
              {brandMark}
              {brandName && (
                <div className="leading-tight">
                  <div className="text-[0.9375rem] font-semibold text-[var(--text-primary)]">
                    {brandName}
                  </div>
                  {brandTagline && (
                    <div className="text-[0.6875rem] text-[var(--text-tertiary)]">
                      {brandTagline}
                    </div>
                  )}
                </div>
              )}
            </div>
            {badge && (
              <span className="rounded-full bg-[var(--warning-soft)] px-2 py-0.5 text-[0.6875rem] font-semibold text-[var(--warning)]">
                {badge}
              </span>
            )}
          </div>

          <div className="rounded-lg border border-[var(--border-default)] bg-[var(--surface-overlay)] p-7 shadow-[var(--shadow-sm)]">
            <div className="mb-5">
              <h1 className="text-[1.125rem] font-semibold text-[var(--text-primary)]">{title}</h1>
              {subtitle && (
                <p className="mt-1 text-[0.75rem] text-[var(--text-tertiary)]">{subtitle}</p>
              )}
            </div>

            {notice && !error && (
              <div
                role="status"
                className="mb-4 flex items-start gap-2 rounded-md border border-[var(--info)]/30 bg-[var(--info-soft)] p-3 text-[0.75rem] text-[var(--text-primary)]"
              >
                <Info size="sm" className="mt-[0.0625rem] flex-none text-[var(--info)]" />
                <span>{notice}</span>
              </div>
            )}

            {error && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 rounded-md border border-[var(--danger)]/30 bg-[var(--danger)]/[0.08] p-3 text-[0.75rem] text-[var(--danger)]"
              >
                <AlertCircle size="sm" className="mt-[0.0625rem] flex-none" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Field label="아이디">
                {(id) => (
                  <input
                    id={id}
                    className={inputClass}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="아이디"
                    autoComplete="username"
                    autoFocus
                  />
                )}
              </Field>

              <Field label="비밀번호">
                {(id) => (
                  <div className="relative">
                    <input
                      id={id}
                      className={cn(inputClass, "pr-9")}
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="비밀번호"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <Eye size="sm" aria-hidden />
                      ) : (
                        <EyeOff size="sm" aria-hidden />
                      )}
                    </button>
                  </div>
                )}
              </Field>

              <label className="flex cursor-pointer items-center gap-2 text-[0.75rem] text-[var(--text-secondary)]">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--brand-primary)]"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span>로그인 정보 기억</span>
              </label>

              <button
                type="submit"
                className="mt-1 inline-flex h-9 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--brand-primary)] px-4 text-[0.8125rem] font-semibold text-white transition-[filter] hover:brightness-95 disabled:opacity-60"
                disabled={loading}
              >
                {loading ? "로그인 중…" : "로그인"}
              </button>
            </form>
          </div>

          {helpText && (
            <div className="mt-5 text-center text-[0.6875rem] text-[var(--text-tertiary)]">
              {helpText}
            </div>
          )}
          {footer && (
            <div className="mt-8 text-center text-[0.6875rem] tracking-wide text-[var(--text-tertiary)]">
              {footer}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/**
 * 좌측 페인 하단의 건설현장 스카이라인. 사진을 깔지 않을 때의 장식이다.
 * 멀리(옅음) · 가까이(짙음) · 지평선 발광 세 겹으로 깊이를 만든다.
 *
 * 배치는 고정값이다 — 난수를 쓰면 렌더마다 바뀌어 브랜드 면이 아니라 노이즈가 된다.
 */
const SKYLINE_BACK: [number, number, number][] = [
  [0, 46, 58],
  [44, 30, 84],
  [72, 38, 66],
  [108, 26, 102],
  [132, 44, 74],
  [174, 34, 120],
  [206, 40, 88],
  [244, 28, 140],
  [270, 46, 96],
  [314, 32, 116],
  [344, 42, 78],
  [384, 30, 104],
  [412, 48, 68],
  [458, 34, 92],
  [490, 40, 72],
  [528, 28, 110],
  [554, 44, 84],
  [596, 32, 64],
  [626, 46, 96],
  [670, 30, 76],
  [698, 42, 110],
  [738, 62, 70],
];
const SKYLINE_FRONT: [number, number, number][] = [
  [-6, 54, 38],
  [46, 36, 62],
  [80, 44, 44],
  [122, 30, 72],
  [150, 50, 50],
  [198, 34, 88],
  [230, 42, 56],
  [270, 56, 40],
  [324, 32, 70],
  [354, 46, 48],
  [398, 38, 64],
  [434, 52, 42],
  [484, 30, 80],
  [512, 44, 54],
  [554, 38, 46],
  [590, 50, 66],
  [638, 34, 50],
  [670, 44, 42],
  [712, 48, 60],
  [758, 42, 44],
];
const SKY_H = 200;

function Skyline() {
  const rects = (list: [number, number, number][]) =>
    list.map(([x, w, h], i) => <rect key={i} x={x} y={SKY_H - h} width={w} height={h} rx="1" />);
  // 창문 — 앞줄 셋 중 하나꼴로만. 전부 찍으면 실루엣이 아니라 격자로 읽힌다
  const windows: React.ReactElement[] = [];
  SKYLINE_FRONT.forEach(([x, w, h], bi) => {
    if (bi % 3 !== 0) return;
    for (let cy = SKY_H - h + 8; cy < SKY_H - 6; cy += 12)
      for (let cx = x + 7; cx < x + w - 5; cx += 11)
        windows.push(
          <rect key={`${bi}-${cx}-${cy}`} x={cx} y={cy} width="3" height="4" rx="0.6" />
        );
  });
  return (
    <>
      {/* 지평선 발광 — 브랜드 주황. 건물 뒤에서 올라오는 노을처럼 깔린다 */}
      <div className="pointer-events-none absolute inset-x-[8%] bottom-12 h-32 bg-[radial-gradient(60%_100%_at_50%_100%,rgba(255,117,0,0.16),transparent_70%)]" />
      <svg
        aria-hidden="true"
        viewBox={`0 0 800 ${SKY_H}`}
        preserveAspectRatio="none"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[17rem] w-full"
      >
        <g fill="#000" opacity="0.27">
          {rects(SKYLINE_BACK)}
        </g>
        <g fill="#000" opacity="0.5">
          {rects(SKYLINE_FRONT)}
        </g>
        <g fill="#fff" opacity="0.1">
          {windows}
        </g>
        {/* 크레인이 없으면 그냥 도시로 읽힌다 */}
        <g color="#000" opacity="0.55">
          <Crane x={214} base={SKY_H - 88} mastH={96} jib={82} counter={30} sw={2} />
          <Crane x={556} base={SKY_H - 66} mastH={74} jib={62} counter={24} sw={1.7} />
        </g>
      </svg>
      {/* 베일 — 실루엣이 문구 쪽으로 올라오지 않게 누른다 */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-[linear-gradient(180deg,rgba(14,17,22,0)_0%,rgba(14,17,22,0.45)_70%,rgba(14,17,22,0.5)_100%)]" />
    </>
  );
}

/** 타워크레인. 선으로 그려야 격자 구조가 보여 건물 실루엣과 구분된다. */
function Crane({
  x,
  base,
  mastH,
  jib,
  counter,
  sw,
}: {
  x: number;
  base: number;
  mastH: number;
  jib: number;
  counter: number;
  sw: number;
}) {
  const top = base - mastH;
  const mw = 7;
  const hook = x + jib * 0.62;
  const brace: string[] = [];
  for (let y = base; y > top + 6; y -= 11) {
    brace.push(
      `M${x - mw / 2} ${y}L${x + mw / 2} ${y - 11}M${x + mw / 2} ${y}L${x - mw / 2} ${y - 11}`
    );
  }
  return (
    <g stroke="currentColor" strokeWidth={sw} fill="none" strokeLinecap="square">
      {/* 마스트 */}
      <path
        d={`M${x - mw / 2} ${base}L${x - mw / 2} ${top}M${x + mw / 2} ${base}L${x + mw / 2} ${top}`}
      />
      <path d={brace.join("")} />
      <path d={`M${x - mw / 2} ${top}L${x + mw / 2} ${top}`} />
      {/* 지브 + 카운터지브, A프레임 펜던트 */}
      <path d={`M${x - counter} ${top}L${x + jib} ${top}`} />
      <path d={`M${x} ${top}L${x} ${top - 13}`} />
      <path
        d={`M${x} ${top - 13}L${x - counter + 4} ${top}M${x} ${top - 13}L${x + jib - 6} ${top}`}
      />
      {/* 균형추 */}
      <path
        d={`M${x - counter} ${top}L${x - counter} ${top + 5}L${x - counter + 9} ${top + 5}L${x - counter + 9} ${top}`}
      />
      {/* 후크 */}
      <path
        d={`M${hook} ${top}L${hook} ${top + 22}M${hook - 3} ${top + 22}L${hook + 3} ${top + 22}`}
      />
    </g>
  );
}

const inputClass =
  "h-9 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-[0.8125rem] text-[var(--text-primary)] outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/20";

/** 로고가 없을 때의 폴백 — 브랜드명 첫 글자 박스 */
function InitialMark({ brandName }: { brandName: string }) {
  return (
    <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-md bg-[var(--brand-primary)] text-[0.9375rem] font-bold text-white">
      {brandName.slice(0, 1)}
    </span>
  );
}

/**
 * 라벨과 입력을 `htmlFor`/`id` 로 잇는다. 자식을 함수로 받는 것은 그 id 를
 * 넘겨주기 위해서다 — 이전에는 라벨이 `<span>` 이라 클릭해도 포커스가 가지 않았다.
 */
function Field({ label, children }: { label: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[0.75rem] font-medium text-[var(--text-tertiary)]">
        {label}
      </label>
      {children(id)}
    </div>
  );
}
